
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticProps } from 'next';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import { useState } from 'react';
import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import { HappyUsers } from '@/components/landing/HappyUsers';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import LogosCloud from '@/components/LogosCloud';
import Seo from '@/components/Seo';
import Testimonials from '@/components/Testimonials';
import { HAS_FREE_PLAN } from '@/constants/common';
import GoogleSvg from '~/svg/google.svg';
const ACCENT = '#a78bfa';
const SHOWCASE_CDN = 'https://media.easy-peasy.ai/landing/gemini-omni-1-1';
const GENERATOR_URL = '/ai-videos?model=Gemini%20Omni%20Flash%201.1';
const EXTEND_URL = '/ai-videos?tab=extend-video';
const OFFICIAL_URL = 'https://blog.google/innovation-and-ai/technology/developers-tools/build-with-gemini-omni-1-1-flash/';
const CheckIcon = () => (<svg className='h-4 w-4 flex-shrink-0 text-violet-300' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7'/>
  </svg>);
const SectionLabel = ({ children }: {
    children: React.ReactNode;
}) => (<span className='mb-4 inline-block text-xs font-bold uppercase tracking-[0.22em]' style={{ color: ACCENT }}>
    {children}
  </span>);
type VideoExample = {
    src: string;
    poster: string;
    label: string;
    description: string;
};
/**
 * Official Gemini Omni 1.1 Flash examples from Google's 2026-08-27 launch
 * post. The Google-hosted masters were downloaded, resized to 1280px wide,
 * encoded as H.264 CRF 28 + AAC 96k with faststart, and re-hosted in the R2
 * `images` bucket under `landing/gemini-omni-1-1/` with poster frames.
 * Native stereo audio is preserved.
 */
const videoExamples: VideoExample[] = [
    {
        src: `${SHOWCASE_CDN}/omni-scene-extension.mp4`,
        poster: `${SHOWCASE_CDN}/omni-scene-extension-poster.jpg`,
        label: 'Multi-Turn Scene Extension',
        description: 'A conversation pulls back through catacombs into a floating library while story and sound stay connected',
    },
    {
        src: `${SHOWCASE_CDN}/omni-camera-extension.mp4`,
        poster: `${SHOWCASE_CDN}/omni-camera-extension-poster.jpg`,
        label: 'Alternative Camera Continuations',
        description: 'One source shot branches into dolly zoom, snap zoom, and frozen-time orbit directions',
    },
    {
        src: `${SHOWCASE_CDN}/omni-first-last-frame.mp4`,
        poster: `${SHOWCASE_CDN}/omni-first-last-frame-poster.jpg`,
        label: 'First & Last Frame Control',
        description: 'Keyframes become a continuous whip-pan transition and a seamless looping camera move',
    },
    {
        src: `${SHOWCASE_CDN}/omni-360p-draft.mp4`,
        poster: `${SHOWCASE_CDN}/omni-360p-draft-poster.jpg`,
        label: '360P Draft Workflow',
        description: 'A lightweight preview lets the microscopic composition be tested before a higher-resolution render',
    },
    {
        src: `${SHOWCASE_CDN}/omni-4k-upscale.mp4`,
        poster: `${SHOWCASE_CDN}/omni-4k-upscale-poster.jpg`,
        label: '1080P & 4K Upscaling',
        description: 'Fish, a chipmunk, and autumn leaves demonstrate the final-delivery resolution ladder',
    },
    {
        src: `${SHOWCASE_CDN}/omni-video-reference.mp4`,
        poster: `${SHOWCASE_CDN}/omni-video-reference-poster.jpg`,
        label: 'Video Motion References',
        description: 'Three character designs inherit distinct dance performances from short reference clips',
    },
];
const VideoShowcase = ({ items, t, }: {
    items: VideoExample[];
    t: (key: string) => string;
}) => {
    const [failed, setFailed] = useState<string[]>([]);
    const visible = items.filter((item) => !failed.includes(item.src));
    if (visible.length === 0)
        return null;
    return (<section id='examples' className='border-t border-white/[0.06] py-24 sm:py-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='mx-auto mb-16 max-w-2xl text-center'>
          <SectionLabel>{t('Official Video Examples')}</SectionLabel>
          <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
            {t('See Gemini Omni 1.1 Flash in Action')}
          </h2>
          <p className='mt-6 text-lg leading-relaxed text-slate-400'>
            {t('Every clip below comes from Google’s official Omni 1.1 release. Press play with sound to see how control, continuity, and native audio work together.')}
          </p>
          <a href={OFFICIAL_URL} target='_blank' rel='noreferrer' className='mt-5 inline-flex text-sm font-medium text-violet-300 underline decoration-violet-300/30 underline-offset-4 transition-colors hover:text-violet-200'>
            {t('View the official Google launch post ↗')}
          </a>
        </div>

        <div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3'>
          {visible.map((video) => (<article key={video.src} className='group'>
              <div className='relative overflow-hidden rounded-2xl border border-white/[0.08] bg-[#080912]'>
                <video src={video.src} poster={video.poster} controls playsInline preload='none' className='aspect-video w-full object-cover' onError={() => setFailed((current) => current.includes(video.src)
                ? current
                : [...current, video.src])}>
                  {t('Your browser does not support HTML video.')}
                </video>
              </div>
              <div className='mt-3 px-1'>
                <h3 className='text-sm font-semibold text-white'>
                  {t(video.label)}
                </h3>
                <p className='mt-1 text-xs leading-relaxed text-slate-500'>
                  {t(video.description)}
                </p>
              </div>
            </article>))}
        </div>
      </div>
    </section>);
};
export default function GeminiOmni11FlashLandingPage() {
    const { t } = useLocale('gemini-omni-1-1-flash');
    const creationModes = [
        {
            tag: 'T2V',
            title: t('Text to Video'),
            description: t('Describe a scene, shot list, timing, dialogue, and sound. Gemini’s world knowledge helps the generated action make visual and narrative sense.'),
            points: [
                t('3–10 second clips with native audio'),
                t('16:9 landscape or 9:16 portrait'),
            ],
        },
        {
            tag: 'I2V',
            title: t('Image to Video'),
            description: t('Bring a photograph, product shot, illustration, or sketch to life while directing subject motion, camera movement, and environmental sound.'),
            points: [
                t('Output framing follows the source image'),
                t('360P drafts through 4K delivery'),
            ],
        },
        {
            tag: 'KEYFRAMES',
            title: t('First & Last Frame'),
            description: t('Set the precise opening and closing image, then prompt the continuous movement between them for transitions, orbits, reveals, and loops.'),
            points: [
                t('Two-keyframe interpolation'),
                t('Native audio generated with the transition'),
            ],
        },
        {
            tag: 'EDIT',
            title: t('Conversational Video Editing'),
            description: t('Upload a short clip and describe the change. Replace objects, restyle the world, adjust lighting, or refine the result without rebuilding the whole prompt.'),
            points: [
                t('4–10 second source clips on Easy-Peasy.AI'),
                t('Up to 5 supporting reference images'),
            ],
        },
        {
            tag: 'EXTEND',
            title: t('Video Extension'),
            description: t('Continue the end of a shot with new action, camera direction, dialogue, and sound while using up to 10 seconds of prior footage as context.'),
            points: [
                t('Add 3–10 seconds per Easy-Peasy.AI extension'),
                t('Picture and audio continuity across the join'),
            ],
        },
    ];
    const resolutions = [
        {
            value: '360P',
            role: t('Draft'),
            detail: t('The low-cost tier for testing composition and motion'),
            width: '30%',
        },
        {
            value: '720P',
            role: t('Standard'),
            detail: t('Native default resolution for everyday production'),
            width: '52%',
        },
        {
            value: '1080P',
            role: t('Finish'),
            detail: t('Upscaled output for high-resolution delivery'),
            width: '74%',
        },
        {
            value: '4K',
            role: t('Master'),
            detail: t('Upscaled maximum resolution for final exports'),
            width: '100%',
        },
    ];
    const workflow = [
        {
            step: '01',
            title: t('Choose the kind of control'),
            description: t('Start from text, one frame, two keyframes, reference images, an existing clip, or the Extend Video tool.'),
        },
        {
            step: '02',
            title: t('Describe what changes over time'),
            description: t('Direct subject motion, camera movement, lighting, dialogue, music, and timed events—not just the opening image.'),
        },
        {
            step: '03',
            title: t('Draft at the right fidelity'),
            description: t('Use 360P to explore quickly, 720P for the native default, or select an upscaled 1080P or 4K finish.'),
        },
        {
            step: '04',
            title: t('Edit or continue the result'),
            description: t('Use a generated clip as the next source: change one detail conversationally or continue the story from its ending.'),
        },
    ];
    const promptTips = [
        {
            title: t('Name the continuity rule'),
            description: t('Say what must remain untouched when editing or extending. A short preservation instruction gives the change a clear boundary.'),
            example: t('Replace the red car with a silver coupe. Keep the driver, camera movement, street, lighting, and soundtrack unchanged.'),
        },
        {
            title: t('Call for one shot when you need one'),
            description: t('Omni may compose several shots by default. Ask for a continuous, unbroken scene with no cuts when camera continuity matters.'),
            example: t('One continuous handheld shot. The camera circles the chef as steam fills the kitchen. No cuts. Keep the room layout consistent.'),
        },
        {
            title: t('Direct audio inside the prompt'),
            description: t('The soundtrack is generated natively, so specify dialogue, ambience, music, and when each sound enters or stops.'),
            example: t('Distant traffic and soft rain throughout. At 5 seconds she says “I found it.” Music enters only after the door opens.'),
        },
        {
            title: t('Use time ranges for dense ideas'),
            description: t('Divide the clip into simple beats when several actions or visual changes must happen in a specific order.'),
            example: t('[0–3s] Follow the marble. [3–6s] It triggers the lever. [6–10s] Pull back to reveal the whole machine.'),
        },
    ];
    const specs = [
        {
            label: t('Generated duration'),
            value: '3–10s',
            detail: t('Any whole second in the range'),
        },
        {
            label: t('Resolution ladder'),
            value: '360P–4K',
            detail: t('1080P and 4K are upscaled'),
        },
        {
            label: t('Frame rate'),
            value: '24fps',
            detail: t('Across every output tier'),
        },
        {
            label: t('Native audio'),
            value: t('Always'),
            detail: t('Stereo sound generated with the video'),
        },
        {
            label: t('Aspect ratios'),
            value: '2',
            detail: '16:9 · 9:16',
        },
        {
            label: t('Reference images'),
            value: '5',
            detail: t('Easy-Peasy.AI reference workflow'),
        },
        {
            label: t('Edit source'),
            value: '4–10s',
            detail: t('One uploaded clip up to 20 MB'),
        },
        {
            label: t('First & last frame'),
            value: t('Yes'),
            detail: t('Dedicated interpolation workflow'),
        },
        {
            label: t('Provenance'),
            value: 'SynthID',
            detail: t('Invisible watermark on generated video'),
        },
    ];
    const prices = [
        { resolution: '360P', credits: '2', label: t('Draft') },
        { resolution: '720P', credits: '6', label: t('Standard'), featured: true },
        { resolution: '1080P', credits: '9', label: t('Upscaled') },
        { resolution: '4K', credits: '18', label: t('Upscaled') },
    ];
    const useCases = [
        {
            title: t('Creative Iteration'),
            description: t('Generate inexpensive 360P drafts, compare motion and composition, then move the winning direction up the resolution ladder.'),
        },
        {
            title: t('Transitions & Seamless Loops'),
            description: t('Bridge two keyframes with a controlled camera path, or use the same image at both ends to design a loop.'),
        },
        {
            title: t('Product & Brand Editing'),
            description: t('Change one object, surface, environment, or line of visible text while asking the model to preserve the rest of the clip.'),
        },
        {
            title: t('Longer Narrative Sequences'),
            description: t('Continue action, dialogue, music, and camera movement from an existing ending instead of rebuilding the scene from scratch.'),
        },
    ];
    const faqs = [
        {
            question: t('What is Gemini Omni 1.1 Flash?'),
            answer: t('Gemini Omni 1.1 Flash is Google’s production-ready multimodal video generation and editing model, released on August 27, 2026. It combines Gemini’s reasoning and world knowledge with text-to-video, image-to-video, keyframe interpolation, conversational editing, references, extension, and native audio.'),
        },
        {
            question: t('How long can a Gemini Omni 1.1 Flash video be?'),
            answer: t('Fresh generations on Easy-Peasy.AI can be any whole number of seconds from 3 to 10. The Extend Video tool can add another 3–10 seconds to a supported source clip.'),
        },
        {
            question: t('Are 1080P and 4K generated natively?'),
            answer: t('No. Google documents 720P as the native default and describes 1080P and 4K as upscaled outputs. The 360P tier is designed for faster, lower-cost drafts before a final-resolution render.'),
        },
        {
            question: t('Does Gemini Omni 1.1 Flash generate audio?'),
            answer: t('Yes. Every output includes native stereo audio. Prompt for dialogue, effects, music, ambience, and timing as part of the scene. The current API does not expose a silent-generation switch.'),
        },
        {
            question: t('How does conversational video editing work?'),
            answer: t('Choose Gemini Omni Flash 1.1 Reference, upload a 4–10 second source clip, and describe the change in plain language. Add up to 5 reference images when a new character, object, or style needs a visual anchor.'),
        },
        {
            question: t('Can I control both the first and last frame?'),
            answer: t('Yes. Select Gemini Omni Flash 1.1 First-Last Frame, upload the two keyframes, and describe the movement that connects them. This is useful for reveals, camera orbits, match transitions, and seamless loops.'),
        },
        {
            question: t('How much does Gemini Omni 1.1 Flash cost on Easy-Peasy.AI?'),
            answer: t('Generation pricing scales with duration and resolution: 2 credits per second at 360P, 6 at 720P, 9 at 1080P, and 18 at 4K. Totals are rounded to whole credits. Video extension also bills the source duration because the complete clip is rendered again.'),
        },
        {
            question: t('Do generated videos include a watermark?'),
            answer: t('Every Gemini Omni generated video includes Google’s imperceptible SynthID watermark for provenance. It is not a visible logo or corner mark.'),
        },
        {
            question: t('Can I use Gemini Omni videos commercially?'),
            answer: t('Yes. You can use outputs in ads, social posts, product pages, client work, and other commercial projects, subject to the Easy-Peasy.AI Terms of Use, Google’s model policies, and your rights to uploaded source material.'),
        },
    ];
    return (<main className='relative overflow-hidden bg-[#080912] text-white'>
      <Seo templateTitle={t('Gemini Omni 1.1 Flash Video Generator | 4K, Editing & Extension')} description={t('Create and edit videos with Gemini Omni 1.1 Flash. Generate 3–10 second clips from 360P to 4K with native audio, keyframes, references, and extension.')} image={`${SHOWCASE_CDN}/omni-first-last-frame-poster.jpg`}/>

      <section className='relative min-h-[780px] overflow-hidden sm:min-h-[94vh]'>
        <video autoPlay muted loop playsInline preload='metadata' poster={`${SHOWCASE_CDN}/omni-first-last-frame-poster.jpg`} src={`${SHOWCASE_CDN}/omni-first-last-frame.mp4`} className='absolute inset-0 h-full w-full object-cover'/>
        <div className='absolute inset-0 bg-[#080912]/75'/>
        <div className='absolute inset-0 bg-gradient-to-b from-[#080912]/25 via-transparent to-[#080912]'/>
        <div className='absolute left-0 top-[22%] h-px w-[38%] bg-gradient-to-r from-transparent via-blue-400/40 to-transparent'/>
        <div className='absolute bottom-[22%] right-0 h-px w-[45%] bg-gradient-to-r from-transparent via-violet-400/40 to-transparent'/>

        <div className='absolute left-0 top-0 z-30 w-full'>
          <LandingPageNavigation variant='light' forceDark/>
        </div>

        <div className='relative z-20 mx-auto flex min-h-[780px] max-w-7xl items-center justify-center px-6 pb-20 pt-40 text-center sm:min-h-[94vh] lg:px-8'>
          <div className='max-w-5xl'>
            <div className='mb-9 inline-flex items-center gap-3 rounded-full border border-white/15 bg-black/25 px-4 py-2 text-xs font-medium tracking-wide text-slate-200 backdrop-blur-md'>
              <span className='flex h-8 w-8 items-center justify-center rounded-full bg-white'>
                <GoogleSvg className='h-5 w-5' aria-hidden='true'/>
              </span>
              {t("Google's production-ready any-to-video model")}
            </div>

            <h1 className='text-5xl font-black leading-[0.92] tracking-[-0.05em] text-white sm:text-7xl lg:text-[6.6rem]'>
              <span className='block'>Gemini Omni</span>
              <span className='mt-2 block text-violet-300'>1.1 Flash</span>
            </h1>

            <p className='mx-auto mt-8 max-w-3xl text-xl font-semibold leading-relaxed text-slate-100 sm:text-2xl'>
              {t('Create it. Change it. Keep going.')}
            </p>
            <p className='mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg'>
              {t('Generate from text or images, connect first and last frames, edit a clip with words, and extend the story—with native audio from quick 360P drafts to a 4K finish.')}
            </p>

            <div className='mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row'>
              <Link href={GENERATOR_URL} className='group inline-flex items-center justify-center rounded-full bg-violet-300 px-8 py-4 text-base font-bold text-[#171027] shadow-[0_18px_60px_-22px_rgba(167,139,250,0.95)] transition-transform hover:scale-[1.025]'>
                {t('Create with Gemini Omni 1.1')}
                <span className='ml-2 transition-transform group-hover:translate-x-1'>
                  →
                </span>
              </Link>
              <a href='#examples' className='px-4 py-3 text-sm font-semibold text-slate-200 transition-colors hover:text-violet-200'>
                {t('Watch official examples')} ↓
              </a>
            </div>

            <div className='mt-8 flex justify-center'>
              <HappyUsers fromLandingPage={true} className='items-center'/>
            </div>

            <div className='mx-auto mt-14 grid max-w-3xl grid-cols-2 border-y border-white/15 sm:grid-cols-4'>
              {[
            ['4K', t('Maximum finish')],
            ['360P', t('Draft tier')],
            ['3–10s', t('Generated clips')],
            [t('Stereo'), t('Native audio')],
        ].map(([value, label]) => (<div key={label} className='border-white/10 px-3 py-5 even:border-l sm:border-l sm:first:border-l-0'>
                  <div className='text-2xl font-bold text-violet-200'>
                    {value}
                  </div>
                  <div className='mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500'>
                    {label}
                  </div>
                </div>))}
            </div>
          </div>
        </div>
      </section>

      <LogosCloud />

      <section className='border-y border-violet-300/10 bg-violet-300/[0.035] py-24 sm:py-28'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-center'>
            <div>
              <SectionLabel>{t('Draft to Delivery')}</SectionLabel>
              <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
                {t('Spend Detail Only When the Direction Is Right')}
              </h2>
              <p className='mt-6 text-lg leading-relaxed text-slate-400'>
                {t('Explore composition and motion at 360P, move to the native 720P default, then choose an upscaled 1080P or 4K output for delivery. The creative decision stays separate from the finishing decision.')}
              </p>
            </div>
            <div className='space-y-5'>
              {resolutions.map((resolution) => (<div key={resolution.value}>
                  <div className='mb-2 flex items-end justify-between gap-4'>
                    <div>
                      <span className='font-bold text-white'>
                        {resolution.value}
                      </span>
                      <span className='ml-2 text-xs text-violet-300'>
                        {resolution.role}
                      </span>
                    </div>
                    <span className='text-right text-xs text-slate-500'>
                      {resolution.detail}
                    </span>
                  </div>
                  <div className='h-2 rounded-full bg-white/[0.06]'>
                    <div className='h-full rounded-full bg-violet-400' style={{ width: resolution.width }}/>
                  </div>
                </div>))}
            </div>
          </div>
        </div>
      </section>

      <section className='py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-16 max-w-2xl text-center'>
            <SectionLabel>{t('Five Ways to Direct')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('One Model Across the Whole Creative Loop')}
            </h2>
            <p className='mt-6 text-lg leading-relaxed text-slate-400'>
              {t('Omni 1.1 is not only a generator. It can begin from different media, preserve context through an edit, and continue from the end of a shot.')}
            </p>
          </div>

          <div className='grid gap-5 md:grid-cols-2 lg:grid-cols-6'>
            {creationModes.map((mode, index) => (<article key={mode.tag} className={`rounded-2xl border border-white/[0.08] bg-white/[0.025] p-7 ${index < 2 ? 'lg:col-span-3' : 'lg:col-span-2'}`}>
                <span className='text-[10px] font-black tracking-[0.2em] text-violet-300'>
                  {mode.tag}
                </span>
                <h3 className='mt-4 text-xl font-bold text-white'>
                  {mode.title}
                </h3>
                <p className='mt-3 text-sm leading-relaxed text-slate-400'>
                  {mode.description}
                </p>
                <ul className='mt-6 space-y-3'>
                  {mode.points.map((point) => (<li key={point} className='flex items-start gap-2.5 text-sm text-slate-400'>
                      <CheckIcon />
                      <span>{point}</span>
                    </li>))}
                </ul>
              </article>))}
          </div>
        </div>
      </section>

      <VideoShowcase items={videoExamples} t={t}/>

      <section className='border-t border-white/[0.06] py-24 sm:py-32'>
        <div className='mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-2 lg:items-center lg:px-8'>
          <div>
            <SectionLabel>{t('Conversational Editing')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Change One Thing Without Starting Over')}
            </h2>
            <p className='mt-6 text-lg leading-relaxed text-slate-400'>
              {t('Upload the clip, say what should change, and name what should stay untouched. Add visual references when the new object, character, or style needs a precise anchor.')}
            </p>
            <ul className='mt-8 space-y-3'>
              {[
            t('Restyle environments and surfaces'),
            t('Add, remove, or replace a subject'),
            t('Change lighting, camera feel, or visible text'),
            t('Keep motion, timing, and unaffected details consistent'),
        ].map((point) => (<li key={point} className='flex items-start gap-2.5 text-sm text-slate-400'>
                  <CheckIcon />
                  <span>{point}</span>
                </li>))}
            </ul>
          </div>

          <div className='rounded-3xl border border-white/[0.08] bg-white/[0.025] p-5 sm:p-8'>
            <div className='rounded-2xl border border-white/[0.08] bg-[#0d1020] p-5'>
              <div className='text-[10px] font-bold uppercase tracking-[0.18em] text-slate-600'>
                {t('Source clip')}
              </div>
              <div className='mt-3 flex items-center gap-3'>
                <div className='h-12 w-20 rounded-lg bg-[linear-gradient(135deg,#312e81,#172554)]'/>
                <div>
                  <div className='text-sm font-semibold text-white'>
                    product-launch.mp4
                  </div>
                  <div className='text-xs text-slate-500'>8.0s · 16:9</div>
                </div>
              </div>
            </div>
            <div className='my-4 ml-auto max-w-[88%] rounded-2xl rounded-br-sm bg-violet-300 p-4 text-sm leading-relaxed text-[#181126]'>
              {t('Replace the red car with the silver coupe from Image 1. Keep everything else the same.')}
            </div>
            <div className='max-w-[88%] rounded-2xl rounded-bl-sm border border-white/[0.08] bg-white/[0.04] p-4 text-sm leading-relaxed text-slate-300'>
              {t('The car changes. The driver, street, camera path, light, pacing, and soundtrack remain in place.')}
            </div>
          </div>
        </div>
      </section>

      <section className='border-t border-white/[0.06] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='grid gap-6 lg:grid-cols-2'>
            <article className='relative overflow-hidden rounded-3xl border border-white/[0.08] bg-white/[0.025] p-8 sm:p-10'>
              <div className='absolute -right-24 -top-24 h-64 w-64 rounded-full bg-blue-500/[0.09] blur-[80px]'/>
              <SectionLabel>{t('Keyframe Interpolation')}</SectionLabel>
              <h2 className='relative text-3xl font-bold tracking-tight text-white sm:text-4xl'>
                {t('Choose the Opening and the Destination')}
              </h2>
              <p className='relative mt-5 leading-relaxed text-slate-400'>
                {t('Upload a first frame and a last frame, then describe the journey between them. Omni reasons across the whole move instead of treating the destination as an afterthought.')}
              </p>
              <div className='relative mt-8 flex items-center gap-3'>
                <span className='rounded-lg border border-blue-400/20 bg-blue-400/[0.08] px-4 py-3 text-xs font-bold text-blue-200'>
                  {t('FIRST FRAME')}
                </span>
                <span className='h-px flex-1 bg-gradient-to-r from-blue-400 via-violet-400 to-fuchsia-400'/>
                <span className='rounded-lg border border-fuchsia-400/20 bg-fuchsia-400/[0.08] px-4 py-3 text-xs font-bold text-fuchsia-200'>
                  {t('LAST FRAME')}
                </span>
              </div>
            </article>

            <article className='relative overflow-hidden rounded-3xl border border-white/[0.08] bg-white/[0.025] p-8 sm:p-10'>
              <div className='absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-violet-500/[0.1] blur-[80px]'/>
              <SectionLabel>{t('Scene Extension')}</SectionLabel>
              <h2 className='relative text-3xl font-bold tracking-tight text-white sm:text-4xl'>
                {t('Continue from Context, Not Just a Frozen Frame')}
              </h2>
              <p className='relative mt-5 leading-relaxed text-slate-400'>
                {t('Omni 1.1 reads up to 10 seconds of prior footage before generating the continuation. That gives the next segment context for characters, movement, camera, dialogue, ambience, and music.')}
              </p>
              <Link href={EXTEND_URL} className='relative mt-8 inline-flex text-sm font-bold text-violet-300 transition-colors hover:text-violet-200'>
                {t('Open the Extend Video tool')} →
              </Link>
            </article>
          </div>
        </div>
      </section>

      <section className='border-t border-white/[0.06] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-16 max-w-2xl text-center'>
            <SectionLabel>{t('Workflow')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('From First Idea to the Next Version')}
            </h2>
          </div>
          <div className='grid gap-8 sm:grid-cols-2 lg:grid-cols-4'>
            {workflow.map((item) => (<article key={item.step}>
                <div className='text-5xl font-black text-violet-300/20'>
                  {item.step}
                </div>
                <h3 className='mt-3 text-lg font-bold text-white'>
                  {item.title}
                </h3>
                <p className='mt-3 text-sm leading-relaxed text-slate-400'>
                  {item.description}
                </p>
              </article>))}
          </div>
        </div>
      </section>

      <section className='border-t border-white/[0.06] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-16 max-w-2xl text-center'>
            <SectionLabel>{t('Prompt Guide')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Prompt the Change, the Timing, and the Boundaries')}
            </h2>
          </div>
          <div className='grid gap-6 lg:grid-cols-2'>
            {promptTips.map((tip) => (<article key={tip.title} className='rounded-2xl border border-white/[0.08] bg-white/[0.025] p-8'>
                <h3 className='text-lg font-bold text-white'>{tip.title}</h3>
                <p className='mt-3 text-sm leading-relaxed text-slate-400'>
                  {tip.description}
                </p>
                <div className='mt-5 rounded-xl border border-white/[0.06] bg-black/20 p-4'>
                  <div className='mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-600'>
                    {t('Example prompt')}
                  </div>
                  <p className='text-sm italic leading-relaxed text-slate-300'>
                    &ldquo;{tip.example}&rdquo;
                  </p>
                </div>
              </article>))}
          </div>
        </div>
      </section>

      <section className='border-t border-white/[0.06] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-14 max-w-2xl text-center'>
            <SectionLabel>{t('Specifications')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Gemini Omni 1.1 Flash Technical Details')}
            </h2>
          </div>
          <div className='mx-auto grid max-w-6xl grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5'>
            {specs.map((spec) => (<div key={spec.label} className='rounded-2xl border border-white/[0.08] bg-white/[0.025] p-5'>
                <div className='text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-600'>
                  {spec.label}
                </div>
                <div className='mt-2 text-2xl font-bold text-violet-300'>
                  {spec.value}
                </div>
                <div className='mt-1 text-xs leading-relaxed text-slate-500'>
                  {spec.detail}
                </div>
              </div>))}
          </div>
        </div>
      </section>

      <section className='border-t border-white/[0.06] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='grid gap-12 lg:grid-cols-[0.72fr_1.28fr] lg:items-start'>
            <div>
              <SectionLabel>{t('Resolution-Based Pricing')}</SectionLabel>
              <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
                {t('Pay for the Fidelity You Need Now')}
              </h2>
              <p className='mt-6 text-lg leading-relaxed text-slate-400'>
                {t('Generation is priced per output second. Use the draft tier for exploration and reserve the higher-resolution tiers for approved directions.')}
              </p>
            </div>
            <div className='grid grid-cols-2 gap-4 sm:grid-cols-4'>
              {prices.map((price) => (<article key={price.resolution} className={`rounded-2xl border p-5 ${price.featured
                ? 'border-violet-300/40 bg-violet-300/[0.07]'
                : 'border-white/[0.08] bg-white/[0.025]'}`}>
                  <div className='text-xs font-bold tracking-[0.14em] text-slate-500'>
                    {price.resolution}
                  </div>
                  <div className='mt-4 text-3xl font-black text-white'>
                    {price.credits}
                  </div>
                  <div className='text-[10px] text-violet-300'>
                    {t('credits / second')}
                  </div>
                  <div className='mt-4 text-xs text-slate-500'>
                    {price.label}
                  </div>
                </article>))}
            </div>
          </div>
        </div>
      </section>

      <section className='border-t border-white/[0.06] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-16 max-w-2xl text-center'>
            <SectionLabel>{t('Production Use Cases')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Where Omni 1.1 Fits the Workflow')}
            </h2>
          </div>
          <div className='grid gap-5 sm:grid-cols-2'>
            {useCases.map((useCase, index) => (<article key={useCase.title} className='flex gap-5 rounded-2xl border border-white/[0.08] bg-white/[0.025] p-7'>
                <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-violet-300/25 text-xs font-black text-violet-300'>
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div>
                  <h3 className='font-bold text-white'>{useCase.title}</h3>
                  <p className='mt-2 text-sm leading-relaxed text-slate-400'>
                    {useCase.description}
                  </p>
                </div>
              </article>))}
          </div>
          <div className='mt-14 flex justify-center'>
            <ButtonLink href={HAS_FREE_PLAN ? GENERATOR_URL : '/#pricing'} variant='dark' className='px-8 py-3 text-lg'>
              {t('Create with Gemini Omni 1.1')}
            </ButtonLink>
          </div>
        </div>
      </section>

      <div className='bg-white dark:bg-slate-900'>
        <FeaturedLogos />
        <Testimonials />
      </div>

      <section className='border-t border-white/[0.06] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-14 max-w-2xl text-center'>
            <SectionLabel>{t('FAQ')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Frequently Asked Questions')}
            </h2>
            <p className='mt-4 text-lg text-slate-400'>
              {t('Everything you need to know about Gemini Omni 1.1 Flash')}
            </p>
          </div>
          <div className='mx-auto max-w-3xl space-y-4'>
            {faqs.map((faq) => (<details key={faq.question} className='group rounded-xl border border-white/[0.08] bg-white/[0.025]'>
                <summary className='flex cursor-pointer items-center justify-between p-5 font-semibold text-white'>
                  {faq.question}
                  <span className='ml-4 text-violet-300 transition-transform group-open:rotate-45'>
                    +
                  </span>
                </summary>
                <div className='px-5 pb-5'>
                  <p className='text-sm leading-relaxed text-slate-400'>
                    {faq.answer}
                  </p>
                </div>
              </details>))}
          </div>
        </div>
      </section>

      <div className='bg-white dark:bg-slate-900'>
        <CallToAction preset='ai-videos'/>
        <Footer />
      </div>
    </main>);
}
const getStaticProps: GetStaticProps = async () => ({
    props: {},
});

    async function __Next_Translate__getStaticProps__1a13a29a251__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-video-generator/gemini-omni-1-1-flash',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a13a29a251__ as getStaticProps }
  